import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentClinicCustomerId } from "@/hooks/useCurrentClinicCustomerId";

export interface PatientSearchRow {
  owner_id: string;
  pet_id: string;
  owner_name: string;
  pet_name: string;
  pet_record_number: string | null;
  owner_document: string | null;
}

type PatientSearchRpcRow = {
  owner_id: string;
  pet_id: string;
  owner_name: string | null;
  pet_name: string | null;
  pet_record_number: string | null;
  owner_document: string | null;
};

/**
 * Busca pacientes (owners + pets) para vincular à venda no PDV.
 * Retorna linhas do get_owners_with_pets com owner_id, pet_id, owner_name, pet_name, pet_record_number e owner_document.
 */
export function usePatientSearchForSale(searchTerm: string) {
  const { data: customerId } = useCurrentClinicCustomerId();
  const trimmed = searchTerm.trim();

  const isEmptySearch = trimmed.length === 0;

  return useQuery<PatientSearchRow[]>({
    queryKey: ["patient-search-sale", customerId, trimmed],
    enabled: !!customerId,
    queryFn: async () => {
      if (!customerId) return [];

      const { data, error } = await supabase.rpc("get_owners_with_pets", {
        p_customer_id: customerId,
        p_search: isEmptySearch ? null : trimmed,
        p_offset: 0,
        p_limit: isEmptySearch ? 10 : 30,
      }) as { data: PatientSearchRpcRow[] | null; error: unknown };

      if (error) throw error;

      return (data ?? []).map((row) => ({
        owner_id: row.owner_id,
        pet_id: row.pet_id,
        owner_name: row.owner_name ?? "",
        pet_name: row.pet_name ?? "",
        pet_record_number: row.pet_record_number ?? null,
        owner_document: row.owner_document ?? null,
      }));
    },
    staleTime: 1000 * 30,
    gcTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });
}
